/**
 * @fileoverview Profile modal hero.
 * @description The top block of the profile modal: a full-bleed navy cover band with the user's
 * initials avatar overlapping its bottom edge, their display name and, beneath it, their role with
 * the account-status badge beside it. Rendered as the first child of the shared Modal's body, so it
 * cancels the body's own padding with negative margins to reach the dialog's edges. There is no
 * photo here: the identity backend serves no avatar, so the avatar is always the initials circle.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import { statusBadgeVariant } from '@/features/auth/utils/profile-display.utils'
import Badge from '@/shared/components/atoms/Badge'

interface ProfileHeroProps {
  displayName: string
  initials: string
  roleName?: string | undefined
  status?: string | undefined
}

/**
 * @component
 * @param {ProfileHeroProps} props - The identity details to show.
 * @param {string} props.displayName - The user's display name (or their email when none is on
 * file).
 * @param {string} props.initials - Avatar initials.
 * @param {string} [props.roleName] - The user's active role name.
 * @param {string} [props.status] - The account status (ACTIVE, LOCKED, ...), shown as a badge.
 */
function ProfileHero({ displayName, initials, roleName, status }: ProfileHeroProps) {
  const { t } = useTranslation()

  return (
    <div className="-mx-6 -mt-5">
      <div className="h-[84px] bg-accent" />
      <div className="flex items-start gap-4 px-6">
        <div
          aria-hidden="true"
          data-testid="profile-avatar"
          className="-mt-11 flex h-[88px] w-[88px] shrink-0 items-center justify-center rounded-full border-4 border-white bg-primary text-[30px] font-semibold text-white"
        >
          {initials}
        </div>
        <div className="min-w-0 pt-3">
          <p className="truncate text-[20px] font-semibold text-ink" data-testid="profile-name">
            {displayName}
          </p>
          {(roleName || status) && (
            <div className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1">
              {roleName && (
                <p className="truncate text-sm text-slate" data-testid="profile-role">
                  {roleName}
                </p>
              )}
              {status && (
                <Badge variant={statusBadgeVariant(status)} dot data-testid="profile-status">
                  {t(`profile.status.${status}`, { defaultValue: status })}
                </Badge>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ProfileHero
